import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table'
import type { Work } from '@brent/shared/types'
import { useAuth } from '@/contexts/AuthContext'
import { useUpdateWork } from '@/hooks/useWorks'
import { useToast } from '@/hooks/use-toast'
import { isFlagged, isEffectivelyMatched } from '@/lib/confidence'
import { ConfidenceBadge } from '@/components/shared/ConfidenceBadge'
import { QualityDropdown } from '@/components/shared/QualityDropdown'
import { WikidataLink, WikidataSearchLink } from '@/components/shared/WikidataLink'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { Button } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react'
import type { QualityScore } from '@brent/shared/types'

export type WorkFilter = 'all' | 'matched' | 'unmatched' | 'flagged' | 'verified' | 'needs_review'

interface WorksTableProps {
  works: Work[]
  filter: WorkFilter
  onFilterChange: (filter: WorkFilter) => void
}

export function WorksTable({ works, filter, onFilterChange }: WorksTableProps) {
  const navigate = useNavigate()
  const { canEdit } = useAuth()
  const updateWork = useUpdateWork()
  const { toast } = useToast()
  const [sorting, setSorting] = useState<SortingState>([{ id: 'programme_count', desc: true }])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const filteredWorks = useMemo(
    () =>
      works.filter((w) => {
        switch (filter) {
          case 'matched':
            return isEffectivelyMatched(w)
          case 'unmatched':
            return !isEffectivelyMatched(w)
          case 'flagged':
            return isFlagged(w.title_canonical, w.wikidata_label, w.confidence, w.quality_score)
          case 'verified':
            return w.quality_score === 'verified'
          case 'needs_review':
            return w.quality_score === 'needs_review'
          default:
            return true
        }
      }),
    [works, filter],
  )

  const handleQualityChange = useCallback(
    async (work: Work, qualityScore: QualityScore) => {
      try {
        await updateWork.mutateAsync({
          id: work.id,
          input: { quality_score: qualityScore },
        })
        toast({ title: 'Updated', description: `Quality set to ${qualityScore}` })
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update quality',
        })
      }
    },
    [updateWork, toast],
  )

  const handleQidSave = useCallback(
    async (work: Work, qid: string) => {
      try {
        const result = await updateWork.mutateAsync({
          id: work.id,
          input: { wikidata_id: qid },
        })
        if (result.enriched) {
          toast({
            title: 'Enriched',
            description: `Matched to ${result.enriched.label}`,
          })
        } else if (result.cleared) {
          toast({ title: 'Cleared', description: 'Wikidata ID removed' })
        }
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update QID',
        })
      }
    },
    [updateWork, toast],
  )

  const columns: ColumnDef<Work>[] = useMemo(
    () => [
      {
        accessorKey: 'title_canonical',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Title
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div>
            <span className="font-medium">{row.getValue('title_canonical')}</span>
            {row.original.title_short && (
              <span className="ml-2 text-xs text-muted-foreground">
                ({row.original.title_short})
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'composer_name',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Composer
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const work = row.original
          if (work.composer_name) {
            return (
              <span
                className="text-sm text-primary hover:underline cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  if (work.composer_id) navigate(`/composers/${work.composer_id}`)
                }}
              >
                {work.composer_name}
              </span>
            )
          }
          return <span className="text-muted-foreground">-</span>
        },
      },
      {
        accessorKey: 'wikidata_id',
        header: 'QID',
        cell: ({ row }) => {
          const work = row.original
          if (canEdit) {
            return (
              <InlineEdit
                value={work.wikidata_id || ''}
                onSave={(qid) => handleQidSave(work, qid)}
                placeholder="Add QID"
              />
            )
          }
          return <WikidataLink qid={work.wikidata_id} />
        },
      },
      {
        accessorKey: 'wikidata_label',
        header: 'Wikidata Label',
        cell: ({ row }) => {
          const work = row.original
          if (work.wikidata_label) {
            return <WikidataLink qid={work.wikidata_id} label={work.wikidata_label} />
          }
          if (work.title_canonical && !work.wikidata_id) {
            return <WikidataSearchLink text={work.title_canonical} />
          }
          return <span className="text-muted-foreground">-</span>
        },
      },
      {
        accessorKey: 'confidence',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Confidence
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <ConfidenceBadge
            confidence={row.original.confidence}
            qualityScore={row.original.quality_score}
          />
        ),
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.confidence ?? -1
          const b = rowB.original.confidence ?? -1
          return a - b
        },
      },
      {
        accessorKey: 'programme_count',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Performances
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <span className="tabular-nums">{row.getValue('programme_count')}</span>,
      },
      {
        accessorKey: 'quality_score',
        header: 'Status',
        cell: ({ row }) => {
          const work = row.original
          if (canEdit) {
            return (
              <QualityDropdown
                value={work.quality_score}
                onChange={(qs) => handleQualityChange(work, qs)}
                disabled={updateWork.isPending}
              />
            )
          }
          return <span className="text-sm capitalize">{work.quality_score.replace('_', ' ')}</span>
        },
      },
    ],
    [canEdit, handleQidSave, handleQualityChange, updateWork.isPending, navigate],
  )

  const table = useReactTable({
    data: filteredWorks,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: { sorting, columnFilters },
    initialState: {
      pagination: { pageSize: 25 },
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Input
          placeholder="Search works..."
          value={(table.getColumn('title_canonical')?.getFilterValue() as string) ?? ''}
          onChange={(e) => table.getColumn('title_canonical')?.setFilterValue(e.target.value)}
          className="max-w-sm"
        />
        <Select value={filter} onValueChange={(v) => onFilterChange(v as WorkFilter)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="matched">Matched</SelectItem>
            <SelectItem value="unmatched">Unmatched</SelectItem>
            <SelectItem value="flagged">Flagged</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="needs_review">Needs Review</SelectItem>
          </SelectContent>
        </Select>
        <div className="sm:ml-auto text-sm text-muted-foreground">
          {table.getFilteredRowModel().rows.length} of {filteredWorks.length} works
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/works/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No works found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(v) => table.setPageSize(Number(v))}
          >
            <SelectTrigger className="h-8 w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
